import React, { useCallback, useMemo } from "react";
import { connect } from "react-redux";

import MapTool from "@mapmycustomers/shared/enum/map/MapTool";
import MarkerShape from "@mapmycustomers/shared/enum/MarkerShape";
import MarkerSize from "@mapmycustomers/shared/enum/MarkerSize";
import { AnyEntityId, Company, Person } from "@mapmycustomers/shared/types/entity";
import { EntityPin } from "@mapmycustomers/shared/types/map";

import { Feature } from "@app/component/map/FeaturedMap";
import AlertBadgesType from "@app/enum/map/AlertBadgesType";
import MarkerEffect from "@app/enum/MarkerEffect";
import MapTooltip from "@app/scene/map/components/MapTooltip";
import Legend from "@app/scene/map/enums/Legend";
import { getLayerLegendsState } from "@app/scene/map/store/layers/selectors";
import { setPinHover } from "@app/scene/map/store/mapMode/actions";
import { MapModeAnnotationView } from "@app/scene/map/store/mapMode/MapModeState";
import {
  getAnnotation,
  getFocusedEntity,
  getMapViewTool,
  getPinHover,
  getSelectedPin,
  getSelectedRecords,
} from "@app/scene/map/store/mapMode/selectors";
import { getPinLocationEntityId, getVisibleBadges } from "@app/scene/map/store/selectors";
import { getMapSettings } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import colors from "@app/styles/_colors.module.scss";
import useAnalytics from "@app/util/contexts/useAnalytics";
import loggingService from "@app/util/logging";
import { PIN_ZINDEX } from "@app/util/map/consts";
import defaultMapEntryGeometryGetter from "@app/util/map/defaultMapEntryGeometryGetter";
import { mapPinIdGetter } from "@app/util/map/idGetters";
import isValidMapEntry from "@app/util/map/isValidMapEntry";
import defaultEntityStyleGetter from "@app/util/map/markerStyles/defaultEntityStyleGetter";
import typedIdGetter from "@app/util/typedIdGetter";
import { getEntityTypeAnalyticsName } from "@app/util/ui";

const isDifferent = (a: EntityPin, b: EntityPin) =>
  a.id !== b.id ||
  a.data.entity !== b.data.entity ||
  a.data.color !== b.data.color ||
  a.data.shape !== b.data.shape ||
  a.sumAmount !== b.sumAmount ||
  a.data.outOfCadence !== b.data.outOfCadence ||
  a.data.overdueActivitiesCount !== b.data.overdueActivitiesCount ||
  a.data.cadenceDaysOut !== b.data.cadenceDaysOut ||
  (a.data as Company | Person)?.upcomingActivity?.startAt !==
    (b.data as Company | Person)?.upcomingActivity?.startAt ||
  Math.abs(a.region!.latitude - b.region!.latitude) > Number.EPSILON ||
  Math.abs(a.region!.longitude - b.region!.longitude) > Number.EPSILON;

interface Props {
  annotation?: MapModeAnnotationView;
  areBadgesVisible?: boolean;
  entityPins: EntityPin[];
  focusedEntityId?: AnyEntityId;
  grayed: boolean;
  interactive: boolean;
  mapSettings: {
    markerSize: MarkerSize;
    showEntityIcons: boolean;
  };
  onClick?: (entity: EntityPin) => void;
  onDoubleClick?: (entity: EntityPin) => void;
  pinHover?: string;
  pinLocationEntityId?: string;
  selectedPinId?: AnyEntityId;
  selection: Set<string>;
  setPinHover: typeof setPinHover;
  styleGetter?: (item: EntityPin) => google.maps.Data.StyleOptions;
  type?: string;
  visibleBadges: AlertBadgesType[];
  zIndex?: number;
}

const EntityFeature: React.FC<Props> = ({
  annotation,
  areBadgesVisible,
  entityPins,
  focusedEntityId,
  grayed,
  interactive,
  mapSettings,
  onClick,
  onDoubleClick,
  pinHover,
  pinLocationEntityId,
  selectedPinId,
  selection,
  setPinHover,
  styleGetter,
  type = "entities",
  visibleBadges,
  zIndex = PIN_ZINDEX,
}) => {
  const analytics = useAnalytics();

  const handleEntityClick = useCallback(
    (entity: EntityPin, event: google.maps.MapMouseEvent) => {
      event.stop();
      loggingService.debug("entity clicked", { entity, event });
      onClick?.(entity);
      analytics.clicked([`${getEntityTypeAnalyticsName(entity.data.entity, false, true)} Pin`]);
    },
    [analytics, onClick]
  );

  const handleEntityDoubleClick = useCallback(
    (entity: EntityPin, event: google.maps.MapMouseEvent) => {
      loggingService.debug("entity double clicked", { entity, event });
      onDoubleClick?.(entity);
      analytics.clicked([`${getEntityTypeAnalyticsName(entity.data.entity, false, true)} Pin`]);
    },
    [analytics, onDoubleClick]
  );

  const handleEntityMouseOver = useCallback(
    (pin: EntityPin) => {
      if (interactive && !focusedEntityId && pin && !selectedPinId) {
        setPinHover(mapPinIdGetter(pin));
      }
    },
    [interactive, focusedEntityId, selectedPinId, setPinHover]
  );

  const handleEntityMouseLeave = useCallback(() => {
    if (interactive && !focusedEntityId && !selectedPinId) {
      setPinHover(undefined);
    }
  }, [interactive, focusedEntityId, selectedPinId, setPinHover]);

  const entity: EntityPin | undefined = useMemo(() => {
    if (interactive && pinHover) {
      return entityPins.find((pin) => {
        const pinId = mapPinIdGetter(pin);
        return (
          pinId === pinHover &&
          (pin.data.id !== annotation?.entityId || pin.data.entity !== annotation?.entityType)
        );
      });
    }

    return undefined;
  }, [annotation, interactive, entityPins, pinHover]);

  const entityColorGetter = useMemo(
    () => (grayed ? () => ({ color: colors.white, fillColor: colors.disabledGray }) : undefined),
    [grayed]
  );

  const entityEffectGetter = useMemo(
    () => (entityPin: EntityPin) => {
      if (
        annotation?.entityId === entityPin.data.id &&
        annotation?.entityType === entityPin.data.entity
      ) {
        return MarkerEffect.OUTLINE;
      }
      return MarkerEffect.SHADOW;
    },
    [annotation]
  );

  const entityIconGetter = useMemo(
    () => (entityPin: EntityPin) => {
      const pinId = mapPinIdGetter(entityPin);
      const icon = mapSettings.showEntityIcons ? entityPin.data.entity : undefined;
      return selection.has(pinId) ||
        focusedEntityId === entityPin.data.id ||
        selectedPinId === entityPin.data.id
        ? "selected"
        : icon;
    },
    [focusedEntityId, mapSettings.showEntityIcons, selectedPinId, selection]
  );

  const entityStrokeColorGetter = useMemo(
    () => (entityPin: EntityPin) => {
      const pinId = mapPinIdGetter(entityPin);
      return selection.has(pinId) ? colors.primary100 : undefined;
    },
    [selection]
  );

  const entityPinStyleGetter = useMemo(() => {
    return defaultEntityStyleGetter({
      areBadgesVisible,
      colorGetter: entityColorGetter,
      effect: entityEffectGetter,
      icon: entityIconGetter,
      markerSize: mapSettings.markerSize,
      shape: (entityPin: EntityPin) => entityPin.data.shape ?? MarkerShape.SQUARE,
      showEntityIcon: mapSettings.showEntityIcons,
      strokeColorGetter: entityStrokeColorGetter,
      visibleBadges,
      zIndex,
    });
  }, [
    areBadgesVisible,
    entityColorGetter,
    entityEffectGetter,
    entityIconGetter,
    entityStrokeColorGetter,
    mapSettings,
    visibleBadges,
    zIndex,
  ]);

  const entityTooltipPosition = useMemo(
    () =>
      entity?.region
        ? {
            lat: entity.region.latitude,
            lng: entity.region.longitude,
          }
        : undefined,
    [entity]
  );

  // Need to exclude pin which is being visually dragged using Pin Location tool
  // It will be rendered independently
  const filteredEntityPins = useMemo(() => {
    if (pinLocationEntityId) {
      return entityPins.filter(
        (entityPin: EntityPin) =>
          isValidMapEntry(entityPin) && pinLocationEntityId !== mapPinIdGetter(entityPin)
      );
    }
    return entityPins.filter(isValidMapEntry);
  }, [entityPins, pinLocationEntityId]);

  return (
    <>
      <Feature<EntityPin>
        geometryGetter={defaultMapEntryGeometryGetter}
        idGetter={typedIdGetter}
        isDifferent={isDifferent}
        items={filteredEntityPins}
        onClick={handleEntityClick}
        onDoubleClick={handleEntityDoubleClick}
        onMouseLeave={handleEntityMouseLeave}
        onMouseOver={handleEntityMouseOver}
        styleGetter={styleGetter ?? entityPinStyleGetter}
        type={grayed ? `${type}-grayed` : type}
      />
      {interactive && entity && (
        <MapTooltip position={entityTooltipPosition} tooltip={entity.data.name} />
      )}
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  annotation: getAnnotation(state),
  areBadgesVisible: getLayerLegendsState(state)[Legend.ALERTS]?.visible,
  focusedEntityId: getFocusedEntity(state)?.data?.id,
  grayed: !!getPinLocationEntityId(state),
  interactive: !getPinLocationEntityId(state) && getMapViewTool(state) !== MapTool.LASSO,
  mapSettings: getMapSettings(state),
  pinHover: getPinHover(state),
  pinLocationEntityId: getPinLocationEntityId(state),
  selectedPinId: getSelectedPin(state)?.data?.id,
  selection: getSelectedRecords(state),
  visibleBadges: getVisibleBadges(state),
});

const mapDispatchToProps = {
  setPinHover,
};

export default connect(mapStateToProps, mapDispatchToProps)(EntityFeature);
